import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { calendarApi } from './calendarApi';

/**
 * The three pages somebody reaches when they cannot simply sign in and read.
 *
 * Kept together because they share one shape: a short form, one request, and a
 * result that is deliberately the same whether or not the address exists.
 */

const shellStyle = {
  maxWidth: '380px',
  margin: '0 auto',
  padding: '48px 16px'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  fontSize: '16px', // 16px or larger, or iOS zooms the page on focus.
  borderRadius: '6px',
  border: '1px solid var(--color-border)',
  background: 'var(--color-input-bg, var(--color-surface))',
  color: 'var(--color-text)',
  boxSizing: 'border-box',
  marginBottom: '16px'
};

/**
 * Ask for a reset link.
 *
 * SAYS THE SAME THING EITHER WAY. The server answers identically for an address
 * that subscribes and one that does not, and this page must not undo that by
 * rendering two different outcomes. Telling somebody an email is not registered
 * turns this form into a way to find out who subscribes.
 */
export function CalendarForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await calendarApi('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    } catch {
      // Deliberately ignored. An error here must not become a signal either.
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div style={shellStyle}>
        <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem' }}>Check your email</h1>
        <p style={{ color: 'var(--color-text-light)', lineHeight: 1.6 }}>
          If that address has a subscription, a reset link is on its way. The
          link works once and stops working after an hour.
        </p>
        <p style={{ marginTop: '24px' }}>
          <Link to="/login" style={{ color: 'var(--color-primary)' }}>Back to sign in</Link>
        </p>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem', marginBottom: '4px' }}>
        Reset your password
      </h1>
      <p style={{ color: 'var(--color-text-light)', marginTop: 0, marginBottom: '24px' }}>
        We will email you a link.
      </p>

      <form onSubmit={submit}>
        <label htmlFor="forgot-email" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          Email
        </label>
        <input
          id="forgot-email"
          type="email"
          autoComplete="username"
          style={inputStyle}
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', fontSize: '1rem' }}
          disabled={busy}
        >
          {busy ? 'Sending' : 'Send the link'}
        </button>
      </form>

      <p style={{ marginTop: '24px' }}>
        <Link to="/login" style={{ color: 'var(--color-primary)' }}>Back to sign in</Link>
      </p>
    </div>
  );
}

/** Spend the token from the email and choose a new password. */
export function CalendarResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await calendarApi('/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password })
      });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={shellStyle}>
        <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem' }}>Password changed</h1>
        <p style={{ color: 'var(--color-text-light)' }}>You can sign in with it now.</p>
        <button
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', marginTop: '16px' }}
          onClick={() => navigate('/login')}
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem', marginBottom: '24px' }}>
        Choose a new password
      </h1>

      {!token && (
        <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>
          That link is missing its token. Ask for a new one.
        </p>
      )}

      <form onSubmit={submit}>
        <label htmlFor="reset-password" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
          New password
        </label>
        <input
          id="reset-password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          style={inputStyle}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        {error && (
          <p style={{ color: 'var(--color-danger-text, var(--color-text))', marginBottom: '16px' }}>{error}</p>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', fontSize: '1rem' }}
          disabled={busy || !token}
        >
          {busy ? 'Saving' : 'Save password'}
        </button>
      </form>

      <p style={{ marginTop: '24px' }}>
        <Link to="/forgot-password" style={{ color: 'var(--color-primary)' }}>Ask for a new link</Link>
      </p>
    </div>
  );
}

/**
 * Where a cancelled subscriber lands.
 *
 * Offers the product rather than explaining the absence of it. Their account,
 * their password and their digest preferences all still exist, so this is a
 * purchase rather than a signup, and it says so.
 */
export function CalendarResubscribe({ subscriber }) {
  return (
    <div style={{ ...shellStyle, maxWidth: '460px' }}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem', marginBottom: '4px' }}>
        Your subscription has ended
      </h1>
      <p style={{ color: 'var(--color-text-light)', marginTop: 0, lineHeight: 1.6 }}>
        {subscriber && subscriber.email
          ? `The account for ${subscriber.email} is still here, along with your states and your email settings.`
          : 'Your account is still here, along with your states and your email settings.'}
        {' '}Start a new subscription and the calendar comes straight back.
      </p>

      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        background: 'var(--color-surface)',
        padding: '16px',
        margin: '20px 0'
      }}>
        <p style={{ margin: '0 0 8px' }}><strong>$24 per month</strong>, or <strong>$199 per year</strong>.</p>
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
          County by county sale dates and a link to each list as it posts.
          Cancel any time, effective at the end of the period you have paid for.
        </p>
      </div>

      <Link
        to="/subscribe"
        className="btn btn-primary"
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '12px',
          fontSize: '1rem',
          textDecoration: 'none'
        }}
      >
        Subscribe again
      </Link>

      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', marginTop: '20px', lineHeight: 1.6 }}>
        Dates are provided as researched and must be confirmed with the county
        before you travel or bid. See our{' '}
        <Link to="/terms" style={{ color: 'var(--color-primary)' }}>terms</Link> and{' '}
        <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>privacy policy</Link>.
      </p>
    </div>
  );
}
